import React, {useContext, useState} from "react";
import {Box, ResponsiveContext, Text, Tip} from "grommet";
import {useRouter} from "next/router";
import { ThemeContext } from "styled-components";
import { normalizeColor } from "grommet/utils";
import useSWR, { useSWRConfig } from "swr";
import AppLayout from "../../components/layouts/AppLayout";
import TransactionList from "../../components/pages/transactions/TransactionList";
import TransactionDetails from "../../components/pages/transactions/TransactionDetails";
import AddTransactionDrawer from "../../components/pages/transactions/AddTransactionDrawer";
import PendingTransactionDetails from "../../components/pages/transactions/PendingTransactionDetails";
import { ThickLeftBorderContainer, MainButtonWithExtension, ExtensionButton, DisplayInlineBlockBox, UnderlinedText } from "../../utils/styled";
import { silentFetcher } from "../../utils/errors";
import { bulkMutate } from "../../utils/cache";
import {
    faPlusLarge as IconPlus,
    faChevronDown as IconArrowDown,
    faCircleExclamation as IconExclamation,
    faEnvelope as IconEnvelope
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import KeyboardShortcut from "../../components/KeyboardShortcut";
import CopyLinkWidget from "../../components/CopyLinkWidget";
import useKeyboardShortcut from "../../utils/hooks/useKeyboardShortcut";

const remittanceEmail = (process.env.NODE_ENV === 'production')
    ? (process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ? "payments@streamlined.dev" : "payments@streamlined.app")
    : "payments@streamlined.dev";

// Filter params we want to preserve when navigating to/from transaction details
// 'offset' (page position) is included so opening/closing the transaction drawer,
// which rebuilds the URL, doesn't drop the page and snap the list back to page 1.
const FILTER_PARAMS = ['q', 'filter', 'dateFrom', 'dateTo', 'amountMin', 'amountMax', 'offset'];

function Transactions() {
    const router = useRouter();
    const {
        query: { action, type, transactionId, pendingTransactionId, pendingTransactionType },
    } = router;

    // Helper to get current filter params from URL
    const getFilterParams = () => {
        const params = {};
        FILTER_PARAMS.forEach(key => {
            if (router.query[key] !== undefined) {
                params[key] = router.query[key];
            }
        });
        return params;
    };

    // Helper to build URL with filter params preserved
    const buildUrlWithFilters = (baseParams) => {
        const filterParams = getFilterParams();
        const allParams = { ...filterParams, ...baseParams };
        const queryString = new URLSearchParams();
        Object.entries(allParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => queryString.append(key, String(v)));
            } else if (value !== undefined && value !== null) {
                queryString.append(key, String(value));
            }
        });
        const qs = queryString.toString();
        return qs ? `/transactions?${qs}` : '/transactions';
    };

    const {cache, mutate} = useSWRConfig();
    const onDrawerClose = () => {
        bulkMutate(new RegExp(/^\/api\/transactions/), cache, mutate);
        // Preserve filter params when closing drawer
        router.push(buildUrlWithFilters({}), undefined, { scroll: false });
    }
    const [isPendingTxnDataEmpty, setIsPendingTxnDataEmpty] = useState(false);
    const [pendingTransactionIds, setPendingTransactionIds] = useState([]);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    // Keyboard shortcut 'n' to open add transaction drawer
    useKeyboardShortcut('n', () => setShowAddDrawer(true));

    const {data: unknownPaymentsData, error: unknownPaymentsError, mutate: mutateUnknownPayments} = useSWR(
        `/api/company/unknown-payments`,
        silentFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval: (data) => data?.pending_allocation_count > 0 ? 5000 : 0,
            onSuccess: (data) => {
                if (data?.pending_allocation_count === 0) {
                    bulkMutate(new RegExp(/^\/api\/transactions/), cache, mutate);
                }
            }
        }
    );

    const onPendingTransactionRowClicked = row => {
        // Store pending transaction IDs in sessionStorage for navigation
        if (pendingTransactionIds.length > 0) {
            sessionStorage.setItem('pendingTransactionIds', JSON.stringify(pendingTransactionIds));
        }
        // Preserve filter params when opening drawer
        router.push(buildUrlWithFilters({
            action: 'view',
            pendingTransactionId: row.settlement_id,
            pendingTransactionType: row.settlement_type
        }), undefined, { scroll: false });
    };
    const onRowClicked = row => {
        // Preserve filter params when opening drawer
        router.push(buildUrlWithFilters({
            action: 'view',
            transactionId: row.id
        }), undefined, { scroll: false });
    };
    const onPendingTxnTableDataChange = (pendingTxnData) => {
        setIsPendingTxnDataEmpty(!pendingTxnData || pendingTxnData.length === 0);
        if (pendingTxnData && Array.isArray(pendingTxnData)) {
            const ids = pendingTxnData.map(txn => ({
                id: txn.settlement_id,
                type: txn.settlement_type
            }));
            setPendingTransactionIds(ids);
            sessionStorage.setItem('pendingTransactionIds', JSON.stringify(ids));
        }
    };
    
    return (
        <AppLayout
            title="Transactions"
            hasStickyDesktopPageHeader
            pageControls={(
                <Box direction="row" fill={isMobile ? "horizontal" : undefined}>
                    <MainButtonWithExtension
                        event={{section: 'transaction_page_header', element: 'record_transaction'}}
                        fill={isMobile ? "horizontal" : undefined}
                        icon={<FontAwesomeIcon SelectedIcon={IconPlus} size="xxsmall" />}
                        label={(
                            <Box direction="row" gap="xsmall" align="center" fill={isMobile ? "horizontal" : undefined}>
                                Record transaction
                                {!isMobile && <KeyboardShortcut keys="N" />}
                            </Box>
                        )}
                        secondary
                        hideRightBorder
                        onClick={() => setShowAddDrawer(true)}
                    />
                    <ExtensionButton
                        event={{section: 'transaction_page_header', element: 'more_options'}}
                        responsive={false}
                        dropProps={{
                            align: { top: 'bottom', right: 'right' }
                        }}
                        secondary
                        icon={<FontAwesomeIcon SelectedIcon={IconArrowDown} size="xxsmall" />}
                        dropContent={
                            <Box pad="small" gap="xsmall" width="340px" border>
                                <Box margin={{top: 'xsmall'}}>
                                    <Text>Forward remittance advice via email</Text>
                                </Box>
                                <CopyLinkWidget link={remittanceEmail} icon={IconEnvelope} textProps={{size: 'small', focusIndicator: false}}/>
                                <DisplayInlineBlockBox>
                                    <Tip
                                        content={(
                                            <Box width={{max: 'medium'}} pad="xsmall" gap="xsmall">
                                                <Text>
                                                    If you have an email that contains your payment details, try forwarding it to us!
                                                </Text>
                                                <Text>
                                                    Streamlined automatically picks up relevant information from your email's attachment, and offers suggestions on how to reconcile your transactions.
                                                </Text>
                                            </Box>
                                        )}>
                                        <UnderlinedText
                                            textDecoration="underline"
                                            color="text-weak"
                                            size="small">
                                            How does it work?
                                        </UnderlinedText>
                                    </Tip>
                                </DisplayInlineBlockBox>
                            </Box>
                        }
                    />
                </Box>
            )}>
            <Box flex="grow">
                {unknownPaymentsData && unknownPaymentsData?.unknown_payments_count > 0 &&
                    (<ThemeContext.Consumer>
                        {(theme) => {
                            const borderColor = normalizeColor(theme.global.colors['border'], theme);
                            const color = 'status-warning';
                            const accentColor = normalizeColor(theme.global.colors[color], theme);
                            return (
                                <ThickLeftBorderContainer
                                    pad={{horizontal: "medium"}}
                                    margin={{top: 'small', bottom: 'medium'}}
                                    round={{ corner: 'left', size: 'xsmall' }}
                                    borderColor={borderColor}
                                    accentColor={accentColor}
                                    elevation="medium">
                                    <TransactionList
                                        type="settled"
                                        additionalParam={{unknown_payments: true}}
                                        dataTableWrapperProps={{
                                            margin: {vertical: 'small'}
                                        }}
                                        controls={(
                                            <Box direction="row" gap="small" align={isMobile ? "start" : "center"} fill="horizontal">
                                                <FontAwesomeIcon
                                                    color="status-warning"
                                                    SelectedIcon={IconExclamation}
                                                    size="medium"
                                                    iconContainerProps={{
                                                        flex: false,
                                                        margin: {right: isMobile ? 'xsmall' : ''}
                                                    }}
                                                />
                                                <Text weight={500}>
                                                    {unknownPaymentsData.unknown_payments_count === 1
                                                        ? "1 payment couldn't be matched to an open invoice"
                                                        : `${unknownPaymentsData.unknown_payments_count} payments couldn't be matched to an open invoice`}
                                                </Text>
                                            </Box>
                                        )}
                                        margin={{top: '0'}}
                                        hideFees={true}
                                        hideSearch
                                        hideDateRangeFilter={true}
                                        hideAmountRangeFilter={true}
                                        // Every row here is unmatched by definition, so the
                                        // Invoice cell could only ever repeat the same warning.
                                        hideInvoice
                                        // A short review queue doesn't warrant a CSV control,
                                        // and its icon reads as "sign out" in the corner.
                                        hideExport
                                        allowDismiss
                                        onMatch={row => router.push(`/transactions/${row.id}/reconciliation`)}
                                        onDismissed={() => {
                                            // This count decides whether the card renders at
                                            // all, and it comes from a different endpoint than
                                            // the list, so dismissing the last unmatched
                                            // payment would otherwise leave an empty card
                                            // behind.
                                            //
                                            // Adjusted locally rather than revalidated: a
                                            // refetch here would run the onSuccess above,
                                            // which bulkMutates every /api/transactions key
                                            // whenever nothing is allocating. That would
                                            // refetch the very list the dismiss just pruned,
                                            // and the search index lag is the whole reason
                                            // for pruning it locally in the first place.
                                            mutateUnknownPayments(
                                                (previous) => previous && {
                                                    ...previous,
                                                    unknown_payments_count: Math.max(
                                                        0,
                                                        (previous.unknown_payments_count || 0) - 1
                                                    ),
                                                },
                                                { revalidate: false }
                                            );
                                        }}
                                        onRowClicked={row => {
                                            if (row.allocation_pending) return;
                                            router.push(`/transactions/${row.id}/reconciliation`);
                                        }} />
                                </ThickLeftBorderContainer>
                            );
                        }}
                    </ThemeContext.Consumer>)
                }
                <Box>
                    <TransactionList
                        title="Pending transactions"
                        type="pending"
                        pendingTxns
                        hideFees={true}
                        hideTableIfNoData
                        collapsible
                        onRowClicked={onPendingTransactionRowClicked}
                        onDataChange={onPendingTxnTableDataChange} />
                </Box>
                <Box>
                    <TransactionList
                        // hide table title if there is no transaction that needs review or pending txn
                        {...((isPendingTxnDataEmpty && (!unknownPaymentsData || unknownPaymentsData?.unknown_payments_count === 0)) ?
                            {} :
                            {title: 'Transactions'})}
                        type="settled"
                        onRowClicked={onRowClicked}
                        storeNavigationData />
                </Box>
            </Box>
            {showAddDrawer && (
                <AddTransactionDrawer
                    onClose={() => {
                        setShowAddDrawer(false);
                        bulkMutate(new RegExp(/^\/api\/transactions/), cache, mutate);
                    }}
                    onSuccess={() => {
                        bulkMutate(new RegExp(/^\/api\/transactions/), cache, mutate);
                    }}
                />
            )}
            { action && action === "add-transaction" && type && (
                <AddTransactionDrawer
                    type={type}
                    onClose={onDrawerClose}
                    onSuccess={
                        () => {
                            bulkMutate(new RegExp(/^\/api\/transactions/), cache, mutate);
                        }
                    }
                />
            )}
            { action && action === "view" && transactionId && (
                <TransactionDetails transactionId={transactionId} onClose={onDrawerClose} />
            )}
            { action && action === "view" && pendingTransactionId && (
                <PendingTransactionDetails
                    transactionId={pendingTransactionId}
                    pendingTransactionType={pendingTransactionType}
                    transactionIds={pendingTransactionIds}
                    onClose={onDrawerClose} />
            )}
        </AppLayout>
    );
}
export default Transactions;
